import axios from 'axios'
import config from '../config'
import { store } from '../store'
import {
    logout as logoutAction,
    addFlashMessage,
    showLoader,
    hideLoader
} from '../actions'

const SESSION_EXPIRED_MESSAGE = 'Sesión vencida. Ingresá nuevamente para continuar.'

let isForceLoggingOut = false

const forceLogout = () => {
    if (isForceLoggingOut) return
    isForceLoggingOut = true
    store.dispatch(logoutAction())
    sessionStorage.removeItem('auth')
    sessionStorage.removeItem('patient')
    window.location.href = '/'
}

// NOTE: 🔥 Loader component
// Now with support for multiple requests
// Calling setLoadingSpinner with TRUE will increment the pendingRequest counter and show the loader
// Calling setLoadingSpinner with FALSE will decrement the counter and hide the loader if the counter got to CERO.
const setLoadingSpinner = isLoading => {
    if (isLoading) {
        pendingRequests++
        store.dispatch(showLoader())
    } else if (pendingRequests > 0) {
        pendingRequests--
        if (pendingRequests <= 0) {
            store.dispatch(hideLoader())
        }
    }
}

export const API = axios.create({
    baseURL: config.endpoints.API_URL
})

// NOTE: Dedicated instance for the Turnera backend.

// Pendig Request counter
let pendingRequests = 0

const isAuthLogoutRequest = err =>
    !!err?.config &&
    typeof err.config.url === 'string' &&
    err.config.url.includes('/Auth/Logout')

const isAuthLoginRequest = err =>
    !!err?.config &&
    typeof err.config.url === 'string' &&
    err.config.url.includes('/Auth/Login')

// NOTE: Setting Authorization token in each request
const requestInterceptor = config => {
    setLoadingSpinner(true)
    const { auth } = store.getState()
    const valtoken = 'accessToken' in auth ? `Valtoken ${auth.accessToken}` : ''
    config.headers = {
        Authorization: valtoken,
        accept: '*/*'
    }
    return config
}

const requestErrorInterceptor = err => err

const responseInterceptor = res => {
    setLoadingSpinner(false)
    return res
}

// NOTE: Logout only when session is actually invalid.
// Network and timeout failures should keep the user in place.
const responseErrorInterceptor = err => {
        setLoadingSpinner(false)
        const isLogoutRequest = isAuthLogoutRequest(err)
        const isLoginRequest = isAuthLoginRequest(err)

        if (isLogoutRequest) {
            return Promise.reject(err)
        }

        const status = err?.response?.status || err?.request?.status || 0
        const message = err?.message || ''
        const skipForceLogout = !!err?.config?.skipForceLogout

        let errorMessage = 'Hubo un error al obtener los datos.'

        // Network issues must not invalidate the session.
        if (
            status === 0 ||
            message.includes('Network Error') ||
            message.toLowerCase().includes('timeout') ||
            err?.code === 'ECONNABORTED'
        ) {
            // CORS blocks 401 responses without Access-Control-Allow-Origin,
            // making them indistinguishable from network errors (status 0).
            // If the user has a token and it's not a timeout, treat as session expired.
            const { auth } = store.getState()
            const hasToken = auth && auth.accessToken
            const isTimeout = message.toLowerCase().includes('timeout') || err?.code === 'ECONNABORTED'

            if (hasToken && !isTimeout && !isLoginRequest && !skipForceLogout) {
                forceLogout()
                return Promise.reject(err)
            }

            errorMessage = 'No pudimos conectar con el servidor. Por favor, intentá nuevamente.'
            store.dispatch(addFlashMessage(errorMessage))
            return Promise.reject(err)
        }

        switch (status) {
            case 401: // Unauthorized
                if (!isLoginRequest && !skipForceLogout) {
                    forceLogout()
                    return Promise.reject(err)
                }
                break

            case 403: // Forbidden
                if (!isLoginRequest && !skipForceLogout) {
                    forceLogout()
                    return Promise.reject(err)
                }
                break

            case 404: // Not Found
                break

            default:
                // Others
                if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data
                } else if (
                    typeof err.response.data === 'object' &&
                    typeof err.response.data.message !== 'undefined'
                ) {
                    errorMessage = err.response.data.message
                } else if (err.config.responseType === 'arraybuffer' && 'TextDecoder' in window) {
                    var encoder = new TextDecoder('utf-8')
                    var decodedError = JSON.parse(encoder.decode(err.response.data))
                    errorMessage = decodedError.message
                }
        }

        if (!err.config?.skipErrorMessage) {
            store.dispatch(addFlashMessage(errorMessage))
        }
        return Promise.reject(err)
    }

const applyInterceptors = instance => {
    instance.interceptors.request.use(requestInterceptor, requestErrorInterceptor)
    instance.interceptors.response.use(responseInterceptor, responseErrorInterceptor)
}

applyInterceptors(API)
